/**
 * One order status vocabulary for the CRM (O-gates; docs/audits/ROLLOUT-ORDERS-SHELL.md §3 D2,
 * decided 2026-10-18).
 *
 * The pinned copy of backend utils/orderStatusMap.js. orderStatusMap.test.js evaluates the
 * backend file and fails until the two agree: change one, change the other.
 *
 * Canonical states: pending, confirmed, processing, ready, out_for_delivery, delivered,
 * cancelled. Legacy values are read-mapped, never rewritten: a row stored `shipped` reads as
 * out_for_delivery, prod's `completed` reads as delivered. The server decides what a user may
 * do (GET /api/orders/:id `_permissions`); the transition table here is for display, not a
 * gate.
 */

export const ORDER_STATUSES = [
  'pending', 'confirmed', 'processing', 'ready', 'out_for_delivery', 'delivered', 'cancelled'
];

export const LEGACY_STATUS_ALIASES = {
  shipped: 'out_for_delivery',
  ready_for_delivery: 'ready',
  completed: 'delivered',
  order_confirmed: 'confirmed'
};

export const ADVANCE_TRANSITIONS = {
  pending: ['confirmed'],
  confirmed: ['processing'],
  processing: ['ready', 'out_for_delivery'],
  ready: ['out_for_delivery', 'delivered'],
  out_for_delivery: ['delivered'],
  delivered: [],
  cancelled: []
};

export const CANCELLABLE_STATUSES = ['pending', 'confirmed', 'processing', 'ready'];

/** Display labels: frontend only, not pinned. */
export const ORDER_STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  processing: 'Processing',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled'
};

/** The canonical state a stored value reads as, or null when it is none of them. */
export function canonicalOrderStatus(status) {
  if (typeof status !== 'string') return null;
  const s = status.trim().toLowerCase();
  if (ORDER_STATUSES.includes(s)) return s;
  return LEGACY_STATUS_ALIASES[s] || null;
}

/** The states an order in `status` may advance to. */
export function nextOrderStatuses(status) {
  const c = canonicalOrderStatus(status);
  return c ? [...ADVANCE_TRANSITIONS[c]] : [];
}

/** Whether an order in `status` may be cancelled (who may is the server's `_permissions`). */
export function isCancellable(status) {
  return CANCELLABLE_STATUSES.includes(canonicalOrderStatus(status));
}

/** The label for a stored value; an unrecognised value shows as itself. */
export function orderStatusLabel(status) {
  const c = canonicalOrderStatus(status);
  return c ? ORDER_STATUS_LABELS[c] : (status || '—');
}
